import { Box, Container, Typography, Divider, Paper } from "@mui/material";
import { theme } from "./theme";
import useTranslationState from "./useTranslationState";

function PrivacyPolicy() {
    const translations = useTranslationState({
        privacyPolicyTitle: 'Privacy Policy',
        lastUpdated: 'Last Updated',
        lastUpdatedDate: 'May 1, 2023',
        introduction: 'Introduction',
        introductionText: 'This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website. Please read this Privacy Policy carefully. We reserve the right to make changes to this Privacy Policy at any time and for any reason.',
        informationWeCollect: 'Information We Collect',
        personalData: 'Personal Data',
        personalDataText: 'We may collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and services, when you participate in activities on the site or otherwise when you contact us.',
        usageData: 'Usage Data',
        usageDataText: 'We may also collect information on how the website is accessed and used. This usage data may include information such as your computer\'s Internet Protocol address (IP address), browser type, browser version, the pages of our website that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers, and other diagnostic data.',
        useOfData: 'Use of Your Data',
        useOfDataText: 'We may use the information we collect in various ways, including to provide, operate, and maintain our website, to improve, personalize, and expand our website, to understand and analyze how you use our website, to develop new products, services, features, and functionality.',
        cookies: 'Cookies',
        cookiesText: 'We use cookies and similar tracking technologies to track the activity on our website and we hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier.',
        thirdPartyLinks: 'Third-Party Links',
        thirdPartyLinksText: 'Our website may contain links to other websites that are not operated by us. If you click a third party link, you will be directed to that third party\'s site. We strongly advise you to review the Privacy Policy of every site you visit.',
        contactUs: 'Contact Us',
        contactUsText: 'If you have any questions about this Privacy Policy, please contact us.',
    });

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: theme.palette.primary.dark,
                paddingY: 12,
                minHeight: '100vh',
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        padding: { xs: 3, sm: 5 },
                        border: '1px solid rgba(255,255,255,0.1)',
                        opacity: 0,
                        animation: 'fadeIn 0.8s forwards cubic-bezier(0.23, 1, 0.32, 1)',
                        '@keyframes fadeIn': {
                            '0%': {
                                opacity: 0,
                                transform: 'translateY(10px)',
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'translateY(0)',
                            },
                        },
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            color: 'white',
                            mb: 1,
                            fontWeight: 500,
                            textAlign: 'center',
                        }}
                    >
                        {translations.privacyPolicyTitle}
                    </Typography>
                    
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            textAlign: 'center',
                            mb: 4,
                        }}
                    >
                        {translations.lastUpdated}: {translations.lastUpdatedDate}
                    </Typography>
                    
                    <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.introduction}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.introductionText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.informationWeCollect}
                        </Typography>
                        
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'white',
                                mb: 1,
                                fontWeight: 500,
                            }}
                        >
                            {translations.personalData}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                mb: 3,
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.personalDataText}
                        </Typography>
                        
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'white',
                                mb: 1,
                                fontWeight: 500,
                            }}
                        >
                            {translations.usageData}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.usageDataText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.useOfData}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.useOfDataText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.cookies}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.cookiesText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.thirdPartyLinks}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.thirdPartyLinksText}
                        </Typography>
                    </Box>
                    
                    <Box>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.contactUs}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.contactUsText}
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
}

export default PrivacyPolicy; 